import React, { useState, useEffect } from "react";

import Modal from "../../Modal";
import Form from "../../Form/Form";
import FormInput from "../../Inputs/FormInput";
import FormSelect from "../../Inputs/FormSelect";
import Button from "../../Button";

import styles from "./pageModal.module.css";
import useAlert from "../../../hooks/useAlert";

const PageModal = ({ show, onClose, config, mode, data, onSave }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const { showAlert } = useAlert();

  /**
   * =====================================
   * INITIALIZE FORM DATA
   * =====================================
   */

  const validateForm = () => {
    const errors = {};

    config.form.forEach((field) => {
      const value = formData[field.name];

      // Skip password required validation in Edit mode
      const isPasswordField = field.type === "password";
      const isEditMode = mode === "Edit";

      // Required validation
      if (field.required) {
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (field.type === "file" && !value?.name)
        ) {
          if (!(isEditMode && isPasswordField)) {
            // only skip password in edit
            errors[field.name] = `${field.label} is required`;
          }
        }
      }

      // Email validation (even if optional)
      if (field.type === "email" && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.name] = "Invalid email address";
        }
      }

      // Password length validation only if filled
      if (isPasswordField && value && value.length < 6) {
        errors[field.name] = "Password must be at least 6 characters";
      }
    });

    return errors;
  };

  useEffect(() => {
    if (!config) return;

    if (data && (mode === "Edit" || mode === "View")) {
      setFormData(data);
    } else {
      const initial = {};

      config.form.forEach((field) => {
        initial[field.name] = field.defaultValue || "";
      });

      setFormData(initial);
    }
  }, [data, config, mode]);

  /**
   * =====================================
   * HANDLE CHANGE
   * =====================================
   */

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  /**
   * =====================================
   * HANDLE SUBMIT
   * =====================================
   */

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Show first error via useAlert
      const firstError = Object.values(validationErrors)[0];
      showAlert("error", firstError);
      return;
    }

    console.log("Submitting form:", formData);

    onSave(formData);
  };

  /**
   * =====================================
   * MODE CHECK
   * =====================================
   */

  const readOnly = mode === "View";

  /**
   * =====================================
   * BUTTON LABEL
   * =====================================
   */

  const getActionLabel = () => {
    if (mode === "Add") return "Create";

    if (mode === "Edit") return "Update";

    return "Save";
  };

  /**
   * =====================================
   * TITLE
   * =====================================
   */

  const getTitle = () => {
    if (!config?.title) return mode;

    return `${mode} ${config.title}`;
  };

  /**
   * =====================================
   * RENDER
   * =====================================
   */

  return (
    <Modal isOpen={show} onClose={onClose} title={getTitle()} size="large">
      <Form onSubmit={handleSubmit}>
        <div className={styles.formGrid}>
          {config?.form?.map((field) => {
            /**
             * ============================
             * SELECT FIELD
             * ============================
             */

            if (field.type === "select") {
              return (
                <FormSelect
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  value={formData[field.name] || ""}
                  options={field.options || []}
                  disabled={readOnly}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                />
              );
            }

            /**
             * ============================
             * INPUT FIELD (TEXT, FILE, PASSWORD)
             * ============================
             */

            return (
              <FormInput
                key={field.name}
                label={
                  <>
                    {field.label}{" "}
                    {field.required && <span style={{ color: "red" }}>*</span>}
                  </>
                }
                name={field.name}
                type={field.type} // ✅ IMPORTANT
                error={errors[field.name]} // <-- shows inline error if your FormInput supports it
                accept={
                  field.type === "file"
                    ? ".png,.jpg,.jpeg,application/pdf"
                    : undefined
                }
                value={
                  field.type !== "file" ? formData[field.name] || "" : undefined
                }
                disabled={readOnly}
                onChange={(e) => {
                  if (field.type === "file") {
                    handleChange(
                      field.name,

                      e.target.files[0],
                    );
                  } else {
                    handleChange(
                      field.name,

                      e.target.value,
                    );
                  }
                }}
              />
            );
          })}
        </div>

        {/* ============================
            BUTTONS
        ============================ */}

        <div className={styles.buttonGroup}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="w-25"
          >
            Cancel
          </Button>

          {!readOnly && (
            <Button type="submit" variant="primary" className="w-25">
              {getActionLabel()}
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default PageModal;
